export const HOME_TRUST_BAR = [
  {
    title: "Canadian-first foundation",
    detail: "Built for Canadian families with Canadian-facing terms, support, and hosting choices.",
  },
  {
    title: "Private storage and release controls",
    detail: "Journals, media, and recipient settings stay private until the release path you define is satisfied.",
  },
  {
    title: "Multiple sign-in paths",
    detail: "Use email and password, Google sign-in, or a one-time email link to access your account.",
  },
  {
    title: "Short-lived sessions",
    detail: "Signed-in sessions expire automatically and can be extended only while you are active.",
  },
];

export const HOME_HOW_IT_WORKS_STEPS = [
  {
    step: "01",
    title: "Create your private plan",
    body:
      "Start with the records, wishes, and family instructions you do not want loved ones piecing together under pressure.",
  },
  {
    step: "02",
    title: "Assign people, content, and rules",
    body:
      "Map family members, tag journals, memories, media, and documents to the people they are meant for, and define who should verify, receive, or help carry out each part of your plan.",
  },
  {
    step: "03",
    title: "Prepare release with care",
    body:
      "Use verification-minded release paths so sensitive content is shared with the right people only when the right conditions are met.",
  },
];

export const HOME_LINEAGE_TAGGING_POINTS = [
  "Create a family lineage that mirrors the people your plan is really for.",
  "Tag journals, photos, videos, and documents to a specific family member instead of leaving everything in one undifferentiated vault.",
  "Keep access tied to release rules, so the right stories and records reach the right people at the right time.",
];

export const HOME_LINEAGE_TAGGING_ASSIGNMENTS = [
  {
    type: "Journal",
    title: "Letter for my daughter",
    recipient: "Child",
    release: "After your release path",
  },
  {
    type: "Media",
    title: "Wedding memories album",
    recipient: "Partner",
    release: "Shared with care",
  },
  {
    type: "Document",
    title: "Family instructions",
    recipient: "Parents",
    release: "When needed later",
  },
];

export const HOME_LINEAGE_TAGGING_SIGNAL_CHIPS = [
  "Family map",
  "Recipient tagging",
  "Release-aware access",
];

export const HOME_DIFFERENTIATOR_COLUMNS = [
  {
    eyebrow: "Generic file vault",
    title: "Stores files, but leaves the hard part to your family",
    points: [
      "Uploads documents without much context",
      "Little guidance on who should do what next",
      "No clear planning model for memorialization, closure, or transfer across online services",
      "Family members still have to interpret instructions during a stressful moment",
    ],
  },
  {
    eyebrow: "AfterMe",
    title: "A digital legacy control center for families",
    points: [
      "Combines protected content with trusted contacts, recipients, and release intent",
      "Keeps family instructions, private notes, and account transition planning in one place",
      "Helps you prepare memorialization, closure, export, transfer, or no-action decisions per service",
      "Focuses on family clarity, privacy, and controlled release instead of file storage alone",
    ],
    featured: true,
  },
];

export const HOME_PROTECTED_VAULT_CARDS = [
  {
    title: "Family instructions",
    body:
      "Personal wishes, practical notes, and next-step guidance that help loved ones act with more confidence and less guesswork.",
  },
  {
    title: "Identity and estate records",
    body:
      "The kinds of records families usually need quickly, organized with context so the right person knows what matters first.",
  },
  {
    title: "Account access references",
    body:
      "Service lists, recovery details, and action preferences that help your family understand which accounts to review and how.",
  },
  {
    title: "Private letters and media",
    body:
      "Sensitive messages, memories, and personal media that can be tagged to specific family members and released only when your conditions are met.",
  },
];

export const HOME_ACCOUNT_TRANSITION_CARDS = [
  {
    title: "Social profiles",
    body:
      "Prepare whether a profile should be memorialized, closed, archived, or left untouched, knowing every platform has its own official process.",
  },
  {
    title: "Email and cloud services",
    body:
      "Document whether an account should be exported, transferred, closed, or preserved as a record for the family.",
  },
  {
    title: "Utilities, subscriptions, and memberships",
    body:
      "Track which services should be reviewed quickly, who is responsible, and what proof may be needed before action is taken.",
  },
];

export const HOME_ACCOUNT_TRANSITION_STATUSES = [
  "Planned",
  "Waiting for documents",
  "Ready for contact",
  "Completed",
];

export const HOME_HERO_PLAN_ITEMS = [
  {
    title: "Protected vault",
    body: "Keep letters, family guidance, records, and private media together in one organized place.",
    badge: "Private by default",
  },
  {
    title: "Trusted people",
    body: "Choose who may verify, receive, or help carry out different parts of your plan.",
    badge: "Role-based access",
  },
  {
    title: "Release path",
    body: "Prepare proof-of-life, family proof, and manual review steps for sensitive situations.",
    badge: "Shared with care",
  },
];

export const HOME_HERO_ACCOUNT_ACTIONS = [
  {
    service: "Social profiles",
    action: "Memorialize or close",
  },
  {
    service: "Email and cloud",
    action: "Export or transfer",
  },
  {
    service: "Subscriptions",
    action: "Review and cancel",
  },
];

export const HOME_HERO_SIGNAL_CHIPS = [
  "Private by default",
  "Trusted contacts",
  "Account transition plan",
];

export const HOME_PROOF_OF_LIFE_POINTS = [
  "Use inactivity confirmation and grace periods instead of invasive alive detection.",
  "Keep trusted-contact verification in the loop before sensitive content is released.",
  "Pair release decisions with manual review paths for high-sensitivity situations.",
];

export const HOME_TRUSTED_CONTACT_RULES = [
  {
    title: "Who can verify",
    body:
      "Choose the people who may help confirm a release event, rather than giving everyone the same level of authority.",
  },
  {
    title: "Who receives what",
    body:
      "Designate recipients for specific notes, media, and future actions so access is guided by intent, not guesswork.",
  },
  {
    title: "What stays protected longer",
    body:
      "Some items deserve stricter handling than others. Build your plan around sensitivity, relationship, and timing.",
  },
];

export const HOME_FAQS = [
  {
    question: "Is AfterMe a generic cloud drive?",
    answer:
      "No. It is designed as a digital legacy planning and account transition control center, with private content, trusted contacts, release rules, and family guidance working together.",
  },
  {
    question: "Will AfterMe automatically close every online account for my family?",
    answer:
      "No. Major platforms each have their own official memorialization, closure, export, or transfer process. AfterMe helps you prepare instructions, track intent, and guide the people responsible.",
  },
  {
    question: "Do trusted contacts automatically see everything?",
    answer:
      "No. Trusted contacts are part of your plan, but sensitive content should still follow the release conditions and recipient rules you set.",
  },
  {
    question: "How is proof-of-life handled?",
    answer:
      "AfterMe should be understood as privacy-safe inactivity confirmation and verification planning, not invasive alive detection. Release paths can include grace periods, trusted-contact confirmation, and manual review.",
  },
  {
    question: "Who is AfterMe for?",
    answer:
      "It is for individuals and families who want more than file storage: private instructions, protected records, trusted contacts, and a clearer plan for digital accounts and online profiles.",
  },
];

export const HOME_PRICING_PREVIEW = [
  {
    name: "Monthly",
    price: "$9.99",
    period: "/ month",
    summary: "A gentle monthly option when you want to keep building at your own pace.",
    highlights: [
      "Private journals, letters, and family mapping",
      "Protected media with thoughtful sharing controls",
      "A simple next step when you are ready to keep going",
    ],
  },
  {
    name: "Yearly",
    price: "$99.99",
    period: "/ year",
    summary: "A steadier option for families who want long-term continuity and peace of mind.",
    highlights: [
      "Everything in Monthly plus more room for memories",
      "Higher media storage and priority feature access",
      "Built for families who want a calmer long-term home",
    ],
    featured: true,
  },
];

export const HOW_IT_WORKS_PAGE_STEPS = [
  {
    step: "01",
    title: "Create your account",
    body:
      "Start with a private account you control. AfterMe already supports email and password, Google sign-in, and one-time email links.",
  },
  {
    step: "02",
    title: "Add wishes, records, and private content",
    body:
      "Capture journals, family instructions, protected media, and the records your family may need most when time and clarity matter.",
  },
  {
    step: "03",
    title: "Assign trusted contacts",
    body:
      "Map family members and designate the people who should help verify, receive, or carry out parts of your plan.",
  },
  {
    step: "04",
    title: "Set release rules",
    body:
      "Define who should receive what and under which circumstances, so content is released by intent instead of guesswork.",
  },
  {
    step: "05",
    title: "Choose inactivity or verification paths",
    body:
      "Prepare release around proof-of-life style inactivity confirmation, family proof, or other manual verification-minded workflows.",
  },
  {
    step: "06",
    title: "Prepare account transition actions",
    body:
      "List the services you care about and document whether each one should be memorialized, closed, exported, transferred, or left untouched.",
  },
];

export const HOW_IT_WORKS_PAGE_NOTES = [
  "AfterMe is designed to reduce family confusion, not replace professional legal or estate advice.",
  "Major online services all use their own official process, so AfterMe focuses on planning, tracking, and guidance.",
  "The most sensitive items should always follow the strongest release rules in your plan.",
];

export const HOW_IT_WORKS_OVERVIEW_GROUPS = [
  {
    steps: ["01", "02"],
    title: "Protect what matters",
    body: "Create your account and gather the wishes, records, and private content your family should not have to reconstruct later.",
  },
  {
    steps: ["03", "04"],
    title: "Choose who is involved",
    body: "Assign trusted people, recipients, and release rules so the right people know their role before a difficult moment arrives.",
  },
  {
    steps: ["05", "06"],
    title: "Prepare what happens later",
    body: "Define verification paths and account-transition actions so your plan is more than storage; it becomes guidance.",
  },
];

export const SECURITY_PROTECTED_ITEMS = [
  {
    title: "Private content",
    body:
      "Journals, letters, and personal media stay inside your account instead of being made public or shared by default.",
  },
  {
    title: "Family roles and recipients",
    body:
      "Your family map and recipient choices help keep the right people connected to the right items when the time comes.",
  },
  {
    title: "Release intent",
    body:
      "AfterMe is designed around timing and intent, so sensitive items can stay private until your chosen release conditions are met.",
  },
  {
    title: "Account planning context",
    body:
      "Digital account preferences, family guidance, and related notes can stay together in one place so loved ones are not left guessing later.",
  },
];

export const SECURITY_ACCESS_POINTS = [
  "Your AfterMe account stays behind sign-in, rather than being viewable from the public website.",
  "Adding someone to your plan does not automatically give them access to everything you have stored.",
  "Sensitive journals, memories, and media can stay tied to the recipient and release timing you choose.",
  "Private family guidance stays organized inside your account so it can be reviewed intentionally, not discovered by accident.",
];

export const SECURITY_TRUSTED_CONTACTS = [
  {
    title: "Trusted contacts can",
    points: [
      "Be part of your family plan and future release path",
      "Help confirm that a release event should move forward",
      "Receive specific items when your release rules allow it",
    ],
  },
  {
    title: "Trusted contacts cannot",
    points: [
      "Automatically see everything just because they were added to your plan",
      "Open sensitive content early unless your release rules allow it",
      "Automatically memorialize or close third-party accounts outside each platform's own official process",
    ],
  },
];

export const SECURITY_VERIFICATION_PATHS = [
  "A trusted person can help confirm that a release should begin",
  "Family proof can be reviewed before sensitive items are shared",
  "Account inactivity can be part of the release path before access changes",
];

export const SECURITY_REVIEW_POINTS = [
  "The family members and trusted contacts attached to your plan",
  "Who each journal, memory, or media item is meant for",
  "Which items are intended for later release instead of immediate access",
  "The release structure currently connected to your account",
];

export const SECURITY_REASSURANCE_POINTS = [
  {
    title: "Private by default",
    body: "Your records and memories are meant to stay personal until you decide who should receive them.",
  },
  {
    title: "Trusted people only",
    body: "Adding someone to your plan does not mean they can see everything automatically.",
  },
  {
    title: "Released with care",
    body: "Sensitive content is meant to follow your chosen timing, recipients, and release path.",
  },
];

export const SECURITY_REASSURANCE_FLOW = [
  "Store privately",
  "Choose trusted people",
  "Release with care",
];

export const PAGE_METADATA = {
  home: {
    title: "AfterMe | Family Continuity and Digital Legacy Planning",
    description:
      "Help your family find memories, documents, wishes, and trusted guidance in one secure place when clarity matters most.",
  },
  how: {
    title: "How AfterMe Works | Trusted Contacts, Release Rules, and Digital Planning",
    description:
      "See how AfterMe helps families prepare private records, assign trusted contacts, plan account memorialization workflows, and organize end-of-life digital planning.",
  },
  security: {
    title: "AfterMe Security & Privacy | Plain-English Access and Release Controls",
    description:
      "Learn how AfterMe keeps sensitive family records private, how trusted contacts fit into your plan, and how release is handled with care.",
  },
  plan: {
    title: "AfterMe Pricing | Digital Legacy Planning Plans for Families",
    description:
      "Compare AfterMe Monthly and Yearly plans for secure family document planning, trusted contacts, release rules, and digital legacy organization—with introductory access and cancel-anytime flexibility.",
  },
  terms: {
    title: "AfterMe Terms of Service",
    description:
      "Review the Terms of Service for AfterMe, a Canadian digital legacy planning platform for protected records, family guidance, and release planning.",
  },
  auth: {
    title: "AfterMe Sign In | Access Your Digital Legacy Plan",
    description:
      "Sign in to AfterMe to manage your private digital legacy plan, trusted contacts, family instructions, and release-aware content.",
  },
};
